import { z } from 'zod';
import { unreachableSpots } from './walking';
import { NeedEffects, TRAVEL_MODES, type PublicTravelMode, type TravelMode } from './play';
import { SKILL_KEYS, type SkillKey } from './progress';
import { SET_PIECES } from './set-pieces';
import { DEFAULT_VISIT_RULES, VisitRules } from './visits';
import { DEFAULT_STREAK_RULES, StreakRules } from './streaks';
import { BusinessContent, DEFAULT_BUSINESS, businessProblems } from './business';
import { AirportContent, DEFAULT_AIRPORT, airportProblems } from './airport';
import { DEFAULT_RAIL, RailContent, railProblems } from './rail';
import { DEFAULT_RAILCO, RailCoContent, railCoProblems } from './railco';
import { BankContent, DEFAULT_BANK, bankProblems } from './bank';
import { DEFAULT_HOUSES, HousesContent, housesProblems } from './houses';
import { BettingContent, DEFAULT_BETTING, bettingProblems } from './betting';
import { DEFAULT_HEALTH, HealthContent, healthProblems } from './health';
import { CoursesContent, coursesProblems } from './courses';
import { DEFAULT_COURSES } from './courses-default';
import { DEFAULT_STAFF, StaffContent, staffProblems } from './staff';
import { DEFAULT_CARS, CarsContent, carsProblems } from './cars';
import { DEFAULT_FAMILY, FamilyContent, familyProblems } from './family';
import { DEFAULT_HUSTLE, HustleContent, hustleProblems } from './hustle';
import { DEFAULT_GIST, GistContent, gistProblems } from './gist';
import { DEFAULT_GOV, GovContent, govProblems } from './gov';

// The game's content (places, what is inside them and what can be done there) is data the admin edits, never
// code. It is kept as numbered versions: a draft is edited, checked and published; players only ever see the
// published one; every version is kept so any of them can be brought back. The code holds only the rules.

// every shape a prop can be drawn as: the home's furniture shapes and the venue ones
export const PROP_SHAPES = [
  'bed',
  'wardrobe',
  'shower',
  'toilet',
  'sink',
  'cooker',
  'fridge',
  'table',
  'chair',
  'desk',
  'sofa',
  'armchair',
  'tv',
  'shelf',
  'rug',
  'plant',
  'lamp',
  'poster',
  'counter',
  'stool',
  'stage',
  'tree',
  'bench',
  'stall',
  'treadmill',
  'weights',
  'gate',
  'display',
  'screen',
  'machine',
  'path',
  'crates',
  // anything from the model library (the prop's `model` says which); a plain box until it loads
  'thing',
  // set pieces: a whole station drawn as one thing (the game's set-piece library draws each)
  ...SET_PIECES,
] as const;
export type PropShape = (typeof PROP_SHAPES)[number];

const id = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9_]+$/, 'Use lower-case letters, numbers and _ only');
const colour = z.string().regex(/^#[0-9a-f]{6}$/i, 'Use a colour like #1d4ed8');
const icon = z.string().min(1).max(16);
const metres = (max: number) => z.number().min(-max).max(max);
const size = z.number().min(0.01).max(20);

export const VenueProp = z.object({
  shape: z.enum(PROP_SHAPES),
  x: metres(20),
  z: metres(20),
  w: size,
  d: size,
  h: size,
  colour,
  turn: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  // flat things (paths, rugs) can be walked over
  flat: z.boolean().optional(),
  // a model from the library ("furniture/loungeSofa"); without one the shape's usual model is used
  model: z
    .string()
    .regex(/^[a-z]+\/[A-Za-z0-9_-]+$/)
    .max(80)
    .optional(),
  // which kind of a set piece (a pen of "sheep" or "llamas"), and the words on it (a shop's name)
  variant: z.string().min(1).max(20).optional(),
  label: z.string().min(1).max(24).optional(),
});
export type VenueProp = z.infer<typeof VenueProp>;
// a spot: a tab in the venue bar, and where the character goes to do the activities listed
export const VenueSpot = z.object({
  id,
  label: z.string().min(1).max(40),
  icon,
  x: metres(20),
  z: metres(20),
  activities: z.array(id),
});
export type VenueSpot = z.infer<typeof VenueSpot>;
// the place's staff (bar staff, shop owners, the vicar), the only people the game makes up: everyone else in a
// place is a real player
export const CROWD_POSES = ['stand', 'talk', 'sit', 'dance', 'workout', 'walk', 'cheer', 'work'] as const;
export type CrowdPose = (typeof CROWD_POSES)[number];
const point = z.object({ x: metres(20), z: metres(20) });
export const CrowdMember = z.object({
  x: metres(20),
  z: metres(20),
  // which way they face, in degrees (0 faces the camera's side of the room, +z)
  face: z.number().min(-360).max(360),
  pose: z.enum(CROWD_POSES),
  // what they are doing, shown when tapped ("Bartender", "Watching the band")
  role: z.string().min(1).max(40),
  // a walker goes round these points
  path: z.array(point).min(2).max(6).optional(),
  // how high they sit (a bench, a stool), and how high they stand (on a stage)
  seat: z.number().min(0).max(1.5).optional(),
  y: z.number().min(0).max(3).optional(),
  // a uniform colour (staff)
  outfit: colour.optional(),
});
export type CrowdMember = z.infer<typeof CrowdMember>;
export const LIGHTS = ['warm', 'cool', 'party'] as const;
export const FLOOR_STYLES = ['tiles', 'boards', 'plain', 'stone', 'carpet', 'grass'] as const;
// the ground round a room when a place does not set its own
export const DEFAULT_GROUND = '#b7c79c';
export const Interior = z.object({
  // a park or a market has no walls, just ground
  outdoor: z.boolean(),
  floor: z.object({ w: z.number().min(3).max(30), d: z.number().min(3).max(30) }),
  wallColour: colour,
  floorColour: colour,
  // the ground round the room (a soft green when not set)
  ground: colour.optional(),
  // a window on the side wall (indoors)
  window: z.boolean(),
  sign: z.object({ text: z.string().max(40), background: colour, colour }).nullable(),
  // where you come in
  entrance: z.object({ x: metres(20), z: metres(20) }),
  props: z.array(VenueProp).max(200),
  spots: z.array(VenueSpot).max(20),
  // the changing line under the place name ("Coffee machine queue")
  vibes: z.array(z.string().min(1).max(80)).max(20),
  // the light inside (warm lamps, cool office light, or a club's coloured lights) and what the floor is
  lights: z.enum(LIGHTS).optional(),
  floorStyle: z.enum(FLOOR_STYLES).optional(),
  crowd: z.array(CrowdMember).max(16).optional(),
});
export type Interior = z.infer<typeof Interior>;

export const PlaceContent = z.object({
  id,
  name: z.string().min(1).max(60),
  area: z.string().min(1).max(60),
  icon,
  description: z.string().max(400),
  things: z.array(z.string().min(1).max(60)).max(12),
  // where it really is
  lon: z.number().min(-1).max(1),
  lat: z.number().min(51).max(52),
  badge: z.string().max(20).optional(),
  comingSoon: z.boolean().optional(),
  visible: z.boolean(),
  sortOrder: z.number().int(),
  interior: Interior,
});
export type PlaceContent = z.infer<typeof PlaceContent>;

const effects = NeedEffects;
export const ActivityContent = z.object({
  id,
  placeId: id,
  label: z.string().min(1).max(40),
  icon,
  // seconds of real time it takes
  seconds: z.number().int().min(1).max(3600),
  // money: a price paid, or a wage earned (never both), in pence
  pricePence: z.number().int().min(1).max(100_000_000).optional(),
  wagePence: z.number().int().min(1).max(1_000_000).optional(),
  effects,
  // the least energy it needs
  minEnergy: z.number().int().min(1).max(100).optional(),
  // XP per go for the skills it trains; the first one listed is the skill that makes it go better
  skills: z.partialRecord(z.enum(SKILL_KEYS as [SkillKey, ...SkillKey[]]), z.number().int().min(1).max(100)).optional(),
  // a job: the titles of its five career levels
  job: z.object({ titles: z.array(z.string().min(1).max(40)).length(5) }).optional(),
  // a card that opens a desk instead of taking time: the flights desk at the airport, the station's ticket office,
  // City Hall's ballot (gov.ts)
  opens: z.enum(['flights', 'trains', 'ballot']).optional(),
});
export type ActivityContent = z.infer<typeof ActivityContent>;

// a way to travel (each is shown its own way on the map: on foot, a bus, the DLR, the tube, a cab)
export const TRAVEL_MODE_IDS = Object.keys(TRAVEL_MODES) as [PublicTravelMode, ...PublicTravelMode[]];
export const TravelOption = z.object({
  mode: z.enum(TRAVEL_MODE_IDS),
  label: z.string().min(1).max(20),
  icon,
  farePence: z.number().int().min(0).max(100_000),
  // how long the journey takes, in real seconds
  seconds: z.number().int().min(3).max(600),
  effects,
  // offered to players (a line can be closed for a while)
  visible: z.boolean(),
});
export type TravelOption = z.infer<typeof TravelOption>;
// a journey's way to travel as the rules use it: one of the content's, or the player's own car (cars.ts)
export type JourneyOption = Omit<TravelOption, 'mode'> & { mode: TravelMode };
// the ways to travel the game starts with (content made before travel was content uses these)
export const DEFAULT_TRAVEL: TravelOption[] = TRAVEL_MODE_IDS.map((mode) => ({
  mode,
  label: TRAVEL_MODES[mode].label,
  icon: TRAVEL_MODES[mode].icon,
  farePence: TRAVEL_MODES[mode].farePence,
  seconds: TRAVEL_MODES[mode].seconds,
  effects: { ...TRAVEL_MODES[mode].effects },
  visible: true,
}));

// a meal a player can buy a friend from a chat (Lagos Life's "Buy them food": delivered to their door, it fills
// them up and cheers them up)
export const MealGift = z.object({
  id,
  label: z.string().min(1).max(40),
  icon,
  pricePence: z.number().int().min(1).max(100_000),
  effects,
  // on the menu (a meal can be taken off for a while)
  visible: z.boolean(),
});
export type MealGift = z.infer<typeof MealGift>;
const meal = (id: string, label: string, icon: string, pricePence: number, hunger: number, fun: number): MealGift => ({
  id,
  label,
  icon,
  pricePence,
  effects: { hunger, fun },
  visible: true,
});
// the meals the game starts with (content made before meals were content uses these)
export const DEFAULT_MEALS: MealGift[] = [
  meal('fish_chips', 'Fish & chips', '🐟', 1_200, 70, 10),
  meal('sunday_roast', 'Sunday roast', '🍗', 1_800, 85, 12),
  meal('curry', 'Chicken curry & rice', '🍛', 1_400, 75, 10),
  meal('pie_mash', 'Pie & mash', '🥧', 900, 65, 6),
  meal('jerk_chicken', 'Jerk chicken & rice', '🍗', 1_100, 70, 12),
  meal('full_english', 'Full English breakfast', '🍳', 1_000, 70, 8),
  meal('kebab', 'Kebab & chips', '🥙', 800, 60, 8),
  meal('afternoon_tea', 'Afternoon tea', '🫖', 2_500, 45, 25),
];

export const WorldContent = z.object({
  places: z.array(PlaceContent).min(1).max(200),
  activities: z.array(ActivityContent).max(1000),
  travel: z.array(TravelOption).max(TRAVEL_MODE_IDS.length).optional(),
  meals: z.array(MealGift).max(40).optional(),
  // visiting homes (content made before visits were content uses DEFAULT_VISIT_RULES)
  visits: VisitRules.optional(),
  // streaks between players (content made before streaks were content uses DEFAULT_STREAK_RULES)
  streaks: StreakRules.optional(),
  // players' businesses (content made before businesses were content uses DEFAULT_BUSINESS)
  business: BusinessContent.optional(),
  // the airport and its airline (content made before uses DEFAULT_AIRPORT)
  airport: AirportContent.optional(),
  // King's Cross: the lines, the stops and the fares (content made before uses DEFAULT_RAIL)
  rail: RailContent.optional(),
  // Rail Co.: players' train companies (content made before uses DEFAULT_RAILCO)
  railco: RailCoContent.optional(),
  // the Bank: savings, bonds and their rates (content made before uses DEFAULT_BANK)
  bank: BankContent.optional(),
  // the homes players live in, move to, buy and extend (content made before uses DEFAULT_HOUSES)
  houses: HousesContent.optional(),
  // Bets: the games' limits, the teams and the safer-play rules (content made before uses DEFAULT_BETTING)
  betting: BettingContent.optional(),
  // Health: the sicknesses, how they come and go, and what treatment costs (content made before uses
  // DEFAULT_HEALTH)
  health: HealthContent.optional(),
  // Courses: the courses, their lessons, notes and quizzes, and the rules (content made before uses
  // DEFAULT_COURSES)
  courses: CoursesContent.optional(),
  // Staff: help for the house, their wages and what they do (content made before uses DEFAULT_STAFF)
  staff: StaffContent.optional(),
  // Cars: the showroom's models, prices and the rules of the road (content made before uses DEFAULT_CARS)
  cars: CarsContent.optional(),
  // Family: dates, rings, weddings and couple goals (content made before uses DEFAULT_FAMILY)
  family: FamilyContent.optional(),
  hustle: HustleContent.optional(),
  // Gist: the feed's rules (missing: the defaults)
  gist: GistContent.optional(),
  // Gov: the Mayor's election, pay and policies (missing: the defaults)
  gov: GovContent.optional(),
});
export type WorldContent = z.infer<typeof WorldContent>;

// what the game's rules need to find in the content (they are tied to these ids in code)
export const REQUIRED_CONTENT = {
  places: ['home'],
  activities: ['sleep', 'home_shower', 'cook', 'watch_tv', 'home_loo', 'attend_class'],
} as const;

// problems that stop a version being published (an empty list means it can go out)
export function contentProblems(content: WorldContent): string[] {
  const problems: string[] = [];
  const placeIds = new Set<string>();
  for (const place of content.places) {
    if (placeIds.has(place.id)) problems.push(`Two places have the id "${place.id}".`);
    placeIds.add(place.id);
  }
  const activityById = new Map<string, ActivityContent>();
  for (const activity of content.activities) {
    if (activityById.has(activity.id)) problems.push(`Two activities have the id "${activity.id}".`);
    activityById.set(activity.id, activity);
    if (!placeIds.has(activity.placeId))
      problems.push(`${activity.label} is at a place that does not exist ("${activity.placeId}").`);
    if (activity.pricePence && activity.wagePence) problems.push(`${activity.label} has both a price and a wage.`);
    if (activity.job && !activity.wagePence) problems.push(`${activity.label} is a job but pays no wage.`);
  }
  for (const place of REQUIRED_CONTENT.places)
    if (!placeIds.has(place)) problems.push(`The game needs a place with the id "${place}".`);
  if (content.travel) {
    const modes = new Set<string>();
    for (const option of content.travel) {
      if (modes.has(option.mode)) problems.push(`Two ways to travel are both "${option.mode}".`);
      modes.add(option.mode);
    }
    if (!content.travel.some((option) => option.visible)) problems.push('Players need at least one way to travel.');
  }
  if (content.meals) {
    const meals = new Set<string>();
    for (const item of content.meals) {
      if (meals.has(item.id)) problems.push(`Two meals have the id "${item.id}".`);
      meals.add(item.id);
      if (!(item.effects.hunger ?? 0)) problems.push(`${item.label} does not fill anyone up (give it some hunger).`);
    }
  }
  if (content.business) problems.push(...businessProblems(content.business));
  if (content.airport) problems.push(...airportProblems(content.airport));
  if (content.rail) problems.push(...railProblems(content.rail));
  if (content.railco) problems.push(...railCoProblems(content.railco));
  if (content.bank) problems.push(...bankProblems(content.bank));
  if (content.houses) problems.push(...housesProblems(content.houses));
  if (content.betting) problems.push(...bettingProblems(content.betting));
  if (content.staff) problems.push(...staffProblems(content.staff));
  if (content.cars) problems.push(...carsProblems(content.cars));
  if (content.family)
    problems.push(...familyProblems(content.family, new Set(content.places.map((place) => place.id))));
  if (content.hustle) problems.push(...hustleProblems(content.hustle));
  if (content.gist) problems.push(...gistProblems(content.gist));
  if (content.gov) problems.push(...govProblems(content.gov));
  if (content.courses) {
    problems.push(...coursesProblems(content.courses));
    const jobs = new Set(content.activities.filter((activity) => activity.job).map((activity) => activity.id));
    const lesson = content.activities.find((activity) => activity.id === content.courses!.classActivityId);
    if (!lesson || lesson.placeId !== content.courses.placeId)
      problems.push("Courses: the class must be an activity done at the courses' place.");
    for (const course of content.courses.courses)
      if (course.career && !jobs.has(course.career))
        problems.push(`Courses: ${course.label} leads to "${course.career}", which is not a job.`);
  }
  if (content.health) {
    problems.push(...healthProblems(content.health));
    const doctor = content.activities.find((activity) => activity.id === content.health!.doctorActivityId);
    if (!doctor || doctor.placeId !== content.health.clinicPlaceId)
      problems.push("Health: the doctor's activity must be one done at the clinic.");
  }
  for (const activity of REQUIRED_CONTENT.activities)
    if (!activityById.has(activity)) problems.push(`The game needs an activity with the id "${activity}".`);
  for (const place of content.places) {
    const { interior } = place;
    const { w, d } = interior.floor;
    const inside = (x: number, z: number, halfW = 0, halfD = 0) =>
      Math.abs(x) + halfW <= w / 2 + 0.01 && Math.abs(z) + halfD <= d / 2 + 0.01;
    for (const prop of interior.props) {
      const [fw, fd] = prop.turn === 1 || prop.turn === 3 ? [prop.d, prop.w] : [prop.w, prop.d];
      if (!inside(prop.x, prop.z, fw / 2, fd / 2))
        problems.push(`${place.name}: a ${prop.shape} sticks out of the room.`);
      if (prop.shape === 'thing' && !prop.model) problems.push(`${place.name}: a thing has no model picked.`);
    }
    for (const member of interior.crowd ?? []) {
      if (!inside(member.x, member.z)) problems.push(`${place.name}: someone (${member.role}) is outside the room.`);
      if (member.path?.some((at) => !inside(at.x, at.z)))
        problems.push(`${place.name}: ${member.role} walks outside the room.`);
    }
    if (!inside(interior.entrance.x, interior.entrance.z))
      problems.push(`${place.name}: the door is outside the room.`);
    const spotIds = new Set<string>();
    const listed = new Set<string>();
    for (const spot of interior.spots) {
      if (spotIds.has(spot.id)) problems.push(`${place.name}: two spots have the id "${spot.id}".`);
      spotIds.add(spot.id);
      if (!inside(spot.x, spot.z)) problems.push(`${place.name}: ${spot.label} is outside the room.`);
      for (const activityId of spot.activities) {
        const activity = activityById.get(activityId);
        if (!activity)
          problems.push(`${place.name}: ${spot.label} lists an activity that does not exist ("${activityId}").`);
        else if (activity.placeId !== place.id)
          problems.push(`${place.name}: ${spot.label} lists ${activity.label}, which belongs to another place.`);
        if (listed.has(activityId)) problems.push(`${place.name}: ${activityId} is in two spots.`);
        listed.add(activityId);
      }
    }
    for (const spot of unreachableSpots(interior))
      problems.push(`${place.name}: nobody can walk from the door to ${spot.label}; move the things in the way.`);
  }
  return problems;
}

// ---------- a new edition of the ready-made content, brought into a live game ----------
// Every place the admin has not changed since an earlier edition (`earlier`: every edition before, oldest
// first) takes the new edition's inside; places the admin has changed keep their changes. Places and things to
// do the new edition adds are added (a place an earlier edition had and the admin removed stays removed), and
// its ways to travel when the game has none yet. A thing to do the new edition retires goes only if the admin
// never changed it. Null when there is nothing to bring in.
export function upgradeContent(
  current: WorldContent,
  earlier: WorldContent[],
  after: WorldContent,
): WorldContent | null {
  const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
  const fresh = new Map(after.places.map((place) => [place.id, place]));
  const untouched = (place: PlaceContent) =>
    earlier.some((edition) => same(place.interior, edition.places.find((item) => item.id === place.id)?.interior));
  let changed = false;
  const places = current.places.map((place) => {
    const next = fresh.get(place.id);
    if (!next || same(place.interior, next.interior) || !untouched(place)) return place;
    changed = true;
    return { ...place, interior: next.interior };
  });
  // places new in this edition. One that an edition the game has already taken had is not brought back (the
  // admin removed it); the editions taken are those up to the newest whose own new places the game has (a game
  // still on the first edition gets every place added since, even ones a later edition brought)
  const have = new Set(places.map((place) => place.id));
  const introduced = earlier.map((edition, i) =>
    edition.places
      .map((place) => place.id)
      .filter((id) => !earlier.slice(0, i).some((before) => before.places.some((place) => place.id === id))),
  );
  let reached = 0;
  introduced.forEach((ids, i) => {
    if (ids.some((id) => have.has(id))) reached = i;
  });
  const had = new Set(earlier.slice(0, reached + 1).flatMap((edition) => edition.places.map((place) => place.id)));
  const newPlaces = after.places.filter((place) => !have.has(place.id) && !had.has(place.id));
  places.push(...newPlaces);
  const placeIds = new Set(places.map((place) => place.id));
  // an activity this edition retires goes, unless the admin has changed it since
  const keep = new Set(after.activities.map((activity) => activity.id));
  const retired = (activity: ActivityContent) =>
    !keep.has(activity.id) &&
    earlier.some((edition) =>
      same(
        activity,
        edition.activities.find((item) => item.id === activity.id),
      ),
    );
  const kept = current.activities.filter((activity) => !retired(activity));
  const known = new Set(current.activities.map((activity) => activity.id));
  const added = after.activities.filter((activity) => !known.has(activity.id) && placeIds.has(activity.placeId));
  const travel = current.travel ?? after.travel;
  if (
    !changed &&
    !newPlaces.length &&
    !added.length &&
    kept.length === current.activities.length &&
    travel === current.travel
  )
    return null;
  return {
    places,
    activities: [...kept, ...added],
    ...(travel ? { travel } : {}),
    ...(current.meals ? { meals: current.meals } : {}),
    ...(current.visits ? { visits: current.visits } : {}),
    ...(current.streaks ? { streaks: current.streaks } : {}),
    ...(current.business ? { business: current.business } : {}),
    ...(current.airport ? { airport: current.airport } : {}),
    ...(current.rail ? { rail: current.rail } : {}),
    ...(current.railco ? { railco: current.railco } : {}),
    ...(current.bank ? { bank: current.bank } : {}),
    ...(current.houses ? { houses: current.houses } : {}),
    ...(current.betting ? { betting: current.betting } : {}),
    ...(current.health ? { health: current.health } : {}),
    ...(current.courses ? { courses: current.courses } : {}),
    ...(current.staff ? { staff: current.staff } : {}),
    ...(current.cars ? { cars: current.cars } : {}),
    ...(current.family ? { family: current.family } : {}),
    ...(current.hustle ? { hustle: current.hustle } : {}),
    ...(current.gist ? { gist: current.gist } : {}),
    ...(current.gov ? { gov: current.gov } : {}),
  };
}

// ---------- the catalogue: published content, indexed for the rules and the screens ----------
export type Catalogue = {
  version: number;
  places: PlaceContent[];
  activities: ActivityContent[];
  place: (id: string) => PlaceContent | undefined;
  activity: (id: string) => ActivityContent | undefined;
  activitiesAt: (placeId: string) => ActivityContent[];
  // jobs (activities that build a career), in content order
  jobs: ActivityContent[];
  isJob: (id: string) => boolean;
  // the ways to travel (all of them; players are offered the visible ones)
  travel: TravelOption[];
  travelMode: (mode: string) => TravelOption | undefined;
  // the meals players can buy each other (all of them; players are offered the visible ones)
  meals: MealGift[];
  meal: (id: string) => MealGift | undefined;
  // the rules for visiting homes
  visits: VisitRules;
  // what keeps a streak going
  streaks: StreakRules;
  // players' businesses: the kinds, premises, areas and rules
  business: BusinessContent;
  // the airport: the airline, cabins, destinations and fares
  airport: AirportContent;
  // the station: its lines, stops and fares
  rail: RailContent;
  // Rail Co.: the trains players can buy, their costs and the rules
  railco: RailCoContent;
  // the Bank: savings, bonds and their rates
  bank: BankContent;
  // the homes: kinds, prices and the rules for moving, buying and extending
  houses: HousesContent;
  // Bets: limits, teams and safer-play rules
  betting: BettingContent;
  // Health: sicknesses and treatment
  health: HealthContent;
  // Courses: lessons, quizzes and certificates
  courses: CoursesContent;
  // Staff: roles, wages and what they do
  staff: StaffContent;
  // Cars: the showroom and the rules of the road
  cars: CarsContent;
  // Family: dates, rings, weddings and couple goals
  family: FamilyContent;
  hustle: HustleContent;
  gist: GistContent;
  gov: GovContent;
};
export function buildCatalogue(content: WorldContent, version: number): Catalogue {
  const places = [...content.places].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const placeById = new Map(places.map((place) => [place.id, place]));
  const activityById = new Map(content.activities.map((activity) => [activity.id, activity]));
  const byPlace = new Map<string, ActivityContent[]>();
  for (const activity of content.activities)
    byPlace.set(activity.placeId, [...(byPlace.get(activity.placeId) ?? []), activity]);
  const jobs = content.activities.filter((activity) => activity.job);
  const travel = content.travel ?? DEFAULT_TRAVEL;
  const meals = content.meals ?? DEFAULT_MEALS;
  return {
    version,
    places,
    activities: content.activities,
    place: (key) => placeById.get(key),
    activity: (key) => activityById.get(key),
    activitiesAt: (placeId) => byPlace.get(placeId) ?? [],
    jobs,
    isJob: (key) => Boolean(activityById.get(key)?.job),
    travel,
    travelMode: (mode) => travel.find((option) => option.mode === mode),
    meals,
    meal: (key) => meals.find((item) => item.id === key),
    visits: content.visits ?? DEFAULT_VISIT_RULES,
    streaks: content.streaks ?? DEFAULT_STREAK_RULES,
    business: content.business ?? DEFAULT_BUSINESS,
    airport: content.airport ?? DEFAULT_AIRPORT,
    rail: content.rail ?? DEFAULT_RAIL,
    railco: content.railco ?? DEFAULT_RAILCO,
    bank: content.bank ?? DEFAULT_BANK,
    houses: content.houses ?? DEFAULT_HOUSES,
    betting: content.betting ?? DEFAULT_BETTING,
    health: content.health ?? DEFAULT_HEALTH,
    courses: content.courses ?? DEFAULT_COURSES,
    staff: content.staff ?? DEFAULT_STAFF,
    cars: content.cars ?? DEFAULT_CARS,
    family: content.family ?? DEFAULT_FAMILY,
    hustle: content.hustle ?? DEFAULT_HUSTLE,
    gist: content.gist ?? DEFAULT_GIST,
    gov: content.gov ?? DEFAULT_GOV,
  };
}

// ---------- what goes over the wire ----------
// players: the published content (cached by version)
export const WorldContentReply = z.object({ version: z.number().int(), content: WorldContent });
export type WorldContentReply = z.infer<typeof WorldContentReply>;
// admins: the published version, the draft (if any) with its problems, and the history
export const ContentVersionSummary = z.object({
  id: z.string().uuid(),
  number: z.number().int(),
  status: z.enum(['draft', 'published', 'retired']),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  publishedBy: z.string().nullable(),
  publishedAt: z.string().nullable(),
});
export type ContentVersionSummary = z.infer<typeof ContentVersionSummary>;
export const AdminContentReply = z.object({
  published: z.object({ version: ContentVersionSummary, content: WorldContent }),
  draft: z.object({ version: ContentVersionSummary, content: WorldContent, problems: z.array(z.string()) }).nullable(),
  history: z.array(ContentVersionSummary),
  // the ready-made inside of each place (what "Use the ready-made design" puts back)
  designs: z.record(z.string(), Interior).optional(),
});
export type AdminContentReply = z.infer<typeof AdminContentReply>;
export const SaveDraftBody = z.object({ content: WorldContent, note: z.string().max(200).optional() }).strict();
export type SaveDraftBody = z.infer<typeof SaveDraftBody>;
export const PublishBody = z.object({ note: z.string().max(200).optional() }).strict();
export type PublishBody = z.infer<typeof PublishBody>;
